<template lang="pug">
    include ../../icon/color
    include ../../icon/round

    div(class="_infb", :class="_class", role="alert")
        template(v-if="tone == 'warning'")
            +icon-color-warning
        template(v-else-if="tone == 'ok'")
            +icon-round-ok
        template(v-else-if="tone == 'error'")
            +icon-round-close
        template(v-else)
            +icon-round-info
        div(class="_infm")
            slot
</template>

<script>
    /**
     * A card with an icon and color theme, intended to convey a message about a single subject.
     *
     * Note: Do not use `v-text`, `v-html`, or `v-markdown` to directly populate the message. These directives are meant
     * to operate on elements, not components.
     */
    export default {
        name: 'ib-infobox',

        props: {
            /**
             * Semantic class for both color and icon.
             */
            tone: String,
            /**
             * Appearance modifier: `sm` is supported.
             */
            appearance: String
        },

        computed: {
            _class() {
                return {
                    ['_t' + this.tone]: !!this.tone,
                    ['_e' + this.appearance]: !!this.appearance
                };
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._infb {
        display: flex;
        align-items: center;
        background: fade(@bg-gray, 15%);
        border-inline-start: 5px solid mix(@fg-gray, @bg, 30%);
        border-radius: 3px;
        padding: 14px;

        > svg {
            flex: 0 0 auto;
            width: 32px;
            height: 32px;
            fill: fade(@fg, 50%);
        }

        &._taccent {
            background-color: fade(@bg-accent, 15%);
            border-color: mix(@fg-accent, @bg, 30%);
            > svg {
                fill: @fg-accent;
            }
        }
        &._tok {
            background-color: fade(@bg-ok, 15%);
            border-color: mix(@fg-ok, @bg, 30%);
            color: mix(@fg-ok, @fg-max, 75%);
            > svg {
                fill: @fg-ok;
            }
        }
        &._twarning {
            background-color: fade(@bg-warning, 15%);
            border-color: mix(@fg-warning, @bg, 45%);
            color: mix(@fg-warning, @fg-max, 35%);
        }
        &._terror {
            background-color: fade(@bg-error, 15%);
            border-color: mix(@fg-error, @bg, 30%);
            color: mix(@fg-error, @fg-max, 75%);
            > svg {
                fill: @fg-error;
            }
        }

        &._esm {
            padding: 8px;
            .font-size(@fs7);
            .line-height-auto(@fs7);
            > svg {
                width: 24px;
                height: 24px;
            }
            > ._infm {
                padding: 0 12px;
            }
        }
    }
    ._infm {
        flex: 1 1 auto;
        padding: 0 16px;
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        ib-infobox This is lower priority information text.
    </template>
</ib-example>

<ib-example id="accent" name="Accent">
    <template lang="pug">
        ib-infobox(tone="accent") This is higher priority information text. It may advise the user to take some action.
    </template>
</ib-example>

<ib-example id="warning" name="Warning">
    <template lang="pug">
        ib-infobox(tone="warning") This is warning text.
    </template>
</ib-example>

<ib-example id="ok" name="Ok">
    <template lang="pug">
        ib-infobox(tone="ok") This is text about some successful action.
    </template>
</ib-example>

<ib-example id="error" name="Error">
    <template lang="pug">
        ib-infobox(tone="error") This is text about some unsuccessful action.
    </template>
</ib-example>

<ib-example id="appearance" name="Appearance">
    <template lang="pug">
        ib-infobox(appearance="sm") This is a compact information text box.
    </template>
</ib-example>
